import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flattenBlocks, matchSpokenToScript, scriptTokenStream, tokenize } from '@goanna-im/shared';
import type { Project, ScrollControl } from '@goanna-im/shared';
import {
  createVoiceFollow,
  isSpeechSupported,
  type VoiceFollowController,
} from './voiceFollow.js';

/**
 * Voice-follow, reusable by both the presenter (tablet listens to the talent) and
 * the producer. Runs speech recognition, matches against the script, and moves the
 * reading position via onScroll. Manual control always still works.
 */
/**
 * How far *back* a match may land from our current place, in tokens (~a line or
 * two). Also the backward search radius. Lets the presenter re-read a line, and —
 * together with the manual-reposition resync below — makes voice re-lock after you
 * scroll the script back up by hand.
 */
const BACK_TOLERANCE = 16;
/** Cap on a single forward advance — anti-whoosh against a spurious far match. */
const MAX_ADVANCE = 20;

export function useVoiceFollow(
  project: Project,
  onScroll: (control: ScrollControl) => void,
  /** The authoritative reading anchor's block id — so voice re-locks when the
   *  script is moved by hand (producer transport / the remote). */
  anchorBlockId?: string,
) {
  const supported = isSpeechSupported();
  const [active, setActive] = useState(false);
  const [status, setStatus] = useState('idle');
  const [heard, setHeard] = useState('');
  const [posToken, setPosToken] = useState<number | null>(null);

  const ctrlRef = useRef<VoiceFollowController | null>(null);
  const lastIdx = useRef(0);
  const lastEmit = useRef(-1);
  /** Block id of the last goto WE emitted, so its echo isn't mistaken for a manual move. */
  const selfBlockRef = useRef<string | null>(null);

  const stream = useMemo(() => scriptTokenStream(project), [project]);
  const blocks = useMemo(() => flattenBlocks(project), [project]);
  const streamRef = useRef(stream);
  streamRef.current = stream;
  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;
  const onScrollRef = useRef(onScroll);
  onScrollRef.current = onScroll;

  /** First stream-token index for a block; for a non-spoken (direction) block, the
   *  nearest spoken block at/after it, else the last token of the nearest before. */
  const firstTokenIdxForBlock = useCallback((blockId: string): number | null => {
    const { blockIds } = streamRef.current;
    const direct = blockIds.indexOf(blockId);
    if (direct >= 0) return direct;
    const order = blocksRef.current;
    const bi = order.findIndex((b) => b.id === blockId);
    if (bi < 0) return null;
    for (let k = bi + 1; k < order.length; k += 1) {
      const j = blockIds.indexOf(order[k].id);
      if (j >= 0) return j;
    }
    for (let k = bi - 1; k >= 0; k -= 1) {
      const j = blockIds.lastIndexOf(order[k].id);
      if (j >= 0) return j;
    }
    return null;
  }, []);

  // Manual reposition: when the reading anchor is moved to a DIFFERENT block by
  // something other than our own goto (producer transport, the remote, a section
  // jump), adopt it as the new search origin so voice-follow pulls from there.
  useEffect(() => {
    if (!active || !anchorBlockId) return;
    const { blockIds } = streamRef.current;
    if (anchorBlockId === blockIds[lastIdx.current]) return; // already tracking here
    if (anchorBlockId === selfBlockRef.current) return; // our own move echoing back
    const idx = firstTokenIdxForBlock(anchorBlockId);
    if (idx == null) return;
    lastIdx.current = idx;
    lastEmit.current = -1; // allow an immediate re-emit at the new place
    selfBlockRef.current = null;
  }, [active, anchorBlockId, firstTokenIdxForBlock]);

  const handleTranscript = useCallback((text: string) => {
    setHeard(text.trim().split(/\s+/).slice(-12).join(' '));
    const { tokens, blockIds } = streamRef.current;
    const idx = matchSpokenToScript(tokens, tokenize(text), lastIdx.current, { back: BACK_TOLERANCE });
    // Reject implausible leaps: a real read advances a few words per update, not a
    // giant jump. Anything outside [-BACK_TOLERANCE, +MAX_ADVANCE] is a bad match — hold.
    if (idx < 0 || idx > lastIdx.current + MAX_ADVANCE || idx < lastIdx.current - BACK_TOLERANCE) return;
    lastIdx.current = idx;
    if (idx === lastEmit.current) return;
    lastEmit.current = idx;

    const bid = blockIds[idx];
    if (!bid) return;
    // Fraction of the way through this block's words → centres the spoken word on
    // the reading band (not just the block's top line).
    let s = idx;
    while (s > 0 && blockIds[s - 1] === bid) s -= 1;
    let e = idx;
    while (e < blockIds.length - 1 && blockIds[e + 1] === bid) e += 1;
    const frac = e > s ? (idx - s) / (e - s) : 0;

    selfBlockRef.current = bid;
    onScrollRef.current({ action: 'goto', blockId: bid, value: frac });
    setPosToken(idx);
  }, []);

  const toggle = useCallback(() => {
    if (active) {
      ctrlRef.current?.stop();
      setActive(false);
      return;
    }
    const controller = createVoiceFollow('en-AU', handleTranscript, setStatus);
    if (!controller) {
      setStatus('not supported');
      return;
    }
    ctrlRef.current = controller;
    // Start from wherever the script currently sits (the resync effect fine-tunes
    // to the live anchor once active), not forced back to the top.
    lastIdx.current = anchorBlockId ? firstTokenIdxForBlock(anchorBlockId) ?? 0 : 0;
    lastEmit.current = -1;
    selfBlockRef.current = null;
    controller.start();
    setActive(true);
  }, [active, handleTranscript, anchorBlockId, firstTokenIdxForBlock]);

  useEffect(() => () => ctrlRef.current?.stop(), []);

  const curBlockId = posToken != null ? stream.blockIds[posToken] : null;
  const trackingBlock = curBlockId ? blocks.findIndex((b) => b.id === curBlockId) + 1 : null;

  return { supported, active, status, heard, trackingBlock, totalBlocks: blocks.length, toggle };
}
